/**
 * stageDetect — Wyckoff-cycle stage classifier for a single timeframe.
 *
 * Per the Top-Down design doc (§2.3), classifies the most recent N bars as:
 *
 *   accumulation  — tight range + low volume + EMAs flat/converging
 *                   (smart money quietly building position)
 *   markup        — strong directional close above prior range + volume spike
 *                   + EMA bullish + ADX trending
 *                   (move out of range, public participation)
 *   distribution  — tight range AFTER a markup + volume divergence
 *                   + EMA flattening
 *                   (smart money quietly unloading; price holds, volume fades)
 *   markdown      — strong directional close below prior range + volume spike
 *                   + EMA bearish + ADX trending
 *                   (move down out of range)
 *   unclear       — doesn't cleanly fit any of the above
 *
 * The thresholds are first-pass heuristics tuned for futures index data.
 * v1: ship + tune from logs after 30 days of real usage. See Top-Down §9.1.
 *
 * Inputs: bars[] with OHLC + volume, plus precomputed EMAs (from emas.js)
 * and ADX (from adx.js). Lets callers compute these once at trade time and
 * pass them in, rather than recomputing inside the heuristic.
 *
 * Pure function. No side effects.
 */

import { computeATRAtIndex } from './entryDiscipline.js';

export const DEFAULT_OPTS = Object.freeze({
  windowBars: 20,          // how many recent bars to evaluate
  tightRangeRatio: 0.6,    // range/(ATR * windowBars) below this = "tight"
  strongBodyRatio: 0.65,   // body/range above this = "strong directional"
  adxTrendThreshold: 25,   // ADX above this = "trending regime"
  volumeSpikeRatio: 1.5,   // current volume / volume MA above this = "spike"
  volumeDryUpRatio: 0.7,   // volume MA over window / volume MA before window < this = "fading"
});

/**
 * Helper: range of an inclusive bar slice.
 */
const rangeOf = (bars, fromIdx, toIdx) => {
  let lo = Infinity, hi = -Infinity;
  for (let i = fromIdx; i <= toIdx; i++) {
    const h = Number(bars[i]?.high);
    const l = Number(bars[i]?.low);
    if (Number.isFinite(h) && h > hi) hi = h;
    if (Number.isFinite(l) && l < lo) lo = l;
  }
  return Number.isFinite(hi) && Number.isFinite(lo) ? { hi, lo, range: hi - lo } : null;
};

/**
 * Helper: average bar volume over an inclusive slice.
 */
const avgVolumeOf = (bars, fromIdx, toIdx) => {
  let sum = 0, n = 0;
  for (let i = fromIdx; i <= toIdx; i++) {
    const v = Number(bars[i]?.volume);
    if (Number.isFinite(v) && v >= 0) {
      sum += v;
      n++;
    }
  }
  return n > 0 ? sum / n : null;
};

/**
 * Classify the Wyckoff stage at the END of `bars` (the most recent bar).
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {Object} ctx       Precomputed series (parallel arrays, same length as bars).
 *   - ema21:    Array<{time, value}>  EMA-21 series (from emas.js)
 *   - ema50:    Array<{time, value}>  EMA-50 series (from emas.js)
 *   - adx:      Array<{time, value}>  ADX series (from adx.js)
 * @param {Object} [opts]
 * @returns {'accumulation' | 'markup' | 'distribution' | 'markdown' | 'unclear'}
 */
export const detectStageAt = (bars, ctx = {}, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return 'unclear';
  const W = Math.max(4, Math.floor(Number(opts.windowBars ?? DEFAULT_OPTS.windowBars)));
  if (bars.length < W) return 'unclear';

  const endIdx = bars.length - 1;
  const startIdx = endIdx - W + 1;
  const r = rangeOf(bars, startIdx, endIdx);
  if (!r) return 'unclear';

  const atr = computeATRAtIndex(bars, endIdx, 14);
  if (!Number.isFinite(atr) || atr <= 0) return 'unclear';

  // Tight-range detection: actual range vs theoretical "moving for ATR each bar"
  const expectedTravel = atr * W;
  const rangeTightness = expectedTravel > 0 ? r.range / expectedTravel : null;
  const isTight = Number.isFinite(rangeTightness) && rangeTightness < (opts.tightRangeRatio ?? DEFAULT_OPTS.tightRangeRatio);

  // Strong directional close in current bar?
  const cur = bars[endIdx];
  const body = Math.abs(Number(cur?.close) - Number(cur?.open));
  const barRange = Number(cur?.high) - Number(cur?.low);
  const bodyRatio = barRange > 0 ? body / barRange : 0;
  const strongBody = bodyRatio >= (opts.strongBodyRatio ?? DEFAULT_OPTS.strongBodyRatio);
  const isUpBar = Number(cur?.close) > Number(cur?.open);
  const isDownBar = Number(cur?.close) < Number(cur?.open);

  // Closed above/below the prior range (excluding current bar)?
  const priorRange = startIdx + 1 <= endIdx - 1 ? rangeOf(bars, startIdx + 1, endIdx - 1) : null;
  const closedAbovePriorRange = priorRange && Number(cur?.close) > priorRange.hi;
  const closedBelowPriorRange = priorRange && Number(cur?.close) < priorRange.lo;

  // ADX trending regime?
  const adxNow = Number(ctx.adx?.[endIdx]?.value);
  const isTrending = Number.isFinite(adxNow) && adxNow > (opts.adxTrendThreshold ?? DEFAULT_OPTS.adxTrendThreshold);

  // Volume spike?
  const curVol = Number(cur?.volume);
  // Use the trailing window (excluding current) as the volume baseline.
  const baseAvgVol = avgVolumeOf(bars, startIdx, endIdx - 1);
  const volRatio = (Number.isFinite(curVol) && Number.isFinite(baseAvgVol) && baseAvgVol > 0)
    ? curVol / baseAvgVol : null;
  const volSpike = Number.isFinite(volRatio) && volRatio > (opts.volumeSpikeRatio ?? DEFAULT_OPTS.volumeSpikeRatio);

  // EMA stack stance + slope.
  const ema21 = Number(ctx.ema21?.[endIdx]?.value);
  const ema50 = Number(ctx.ema50?.[endIdx]?.value);
  const emaBullish = Number.isFinite(ema21) && Number.isFinite(ema50) && ema21 > ema50;
  const emaBearish = Number.isFinite(ema21) && Number.isFinite(ema50) && ema21 < ema50;
  const emaConverging = Number.isFinite(ema21) && Number.isFinite(ema50)
    && Math.abs(ema21 - ema50) / Math.max(atr, 1e-9) < 0.5;

  // ── Decision ──────────────────────────────────────────────────────────

  // Markup: strong up bar closing above prior range + volume spike + EMA bullish + ADX trending
  if (strongBody && isUpBar && closedAbovePriorRange && volSpike && emaBullish && isTrending) {
    return 'markup';
  }

  // Markdown: mirror of markup, down direction
  if (strongBody && isDownBar && closedBelowPriorRange && volSpike && emaBearish && isTrending) {
    return 'markdown';
  }

  // Distribution: tight range + EMA flattening + volume FADING vs earlier in
  // the window. The classic Wyckoff signal — price holds while smart money
  // quietly unloads, so volume fades but range stays constrained. Checked
  // BEFORE accumulation because the fading-volume condition is the more
  // specific tell — accumulation is the general "tight + converging" state.
  let isDistribution = false;
  if (isTight && emaConverging) {
    const half = Math.floor(W / 2);
    const front = avgVolumeOf(bars, startIdx, startIdx + half - 1);
    const back = avgVolumeOf(bars, startIdx + half, endIdx);
    isDistribution = Number.isFinite(front) && Number.isFinite(back) && front > 0
      ? (back / front) < (opts.volumeDryUpRatio ?? DEFAULT_OPTS.volumeDryUpRatio)
      : false;
    if (isDistribution) return 'distribution';
  }

  // Accumulation: tight range + EMAs converging + low/normal volume (no spike,
  // and no fading-volume signature that would have flagged distribution above).
  if (isTight && emaConverging && !volSpike) {
    return 'accumulation';
  }

  return 'unclear';
};
